/**
 * Time conversions for the result-editing evaluator.
 *
 * The backend converts every window/schedule time to INTEGER MINUTES:
 *   minutes = minutes_since_midnight + 1440 * dayInt
 * (route-optimizer-back-end app/vrp_data.py convert_column_to_integer). The
 * evaluator must mirror that exactly, so all feasibility math here is done in
 * those integer minutes.
 *
 * Input formats seen in the store:
 *  - table cells ("hh:mm a"  e.g. "02:30 pm") — written by normalizeTimeInput
 *  - result rows ("%H:%M:%S" e.g. "14:30:00") — written by the backend
 * `clockStrToMinutes` accepts both (generalized from the parser that lived in
 * VehicleRoutesDashboard).
 */

const CLOCK_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?$/;

export const MINUTES_PER_DAY = 1440;

/**
 * Parse a clock string ("H:MM", "HH:MM:SS", 12-hour with am/pm) into
 * minutes-since-midnight. Returns null for unparseable input.
 */
export function clockStrToMinutes(value) {
  if (typeof value !== 'string') return null;
  const s = value.trim();
  if (!s) return null;
  const m = s.match(CLOCK_RE);
  if (!m) return null;
  let hours = parseInt(m[1], 10);
  const minutes = parseInt(m[2], 10);
  const seconds = m[3] != null ? parseInt(m[3], 10) : 0;
  const meridiem = m[4] ? m[4].toLowerCase() : null;
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (meridiem === 'pm' && hours < 12) hours += 12;
  if (meridiem === 'am' && hours === 12) hours = 0;
  return hours * 60 + minutes + Math.round(seconds / 60);
}

/**
 * Resolve a window bound to backend integer minutes, honouring the active
 * time unit exactly the way the solve-request path does: integer mode reads
 * the *Int column verbatim; clock mode parses the *Str column and offsets by
 * the *DayInt column. Returns null when the bound is absent (= unconstrained).
 *
 * @param {object} row        table row holding the columns
 * @param {object} cols       {int, str, day} column names
 * @param {string} timeUnit  'integer' | 'clock'
 */
export function windowBoundToMinutes(row, cols, timeUnit) {
  if (!row) return null;
  if (timeUnit === 'integer') {
    const v = row[cols.int];
    return v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v);
  }
  const base = clockStrToMinutes(row[cols.str]);
  if (base == null) return null;
  const day = Number(row[cols.day]);
  return base + (Number.isFinite(day) ? day : 0) * MINUTES_PER_DAY;
}

/**
 * Format integer minutes as the backend's result-row clock format
 * ("%H:%M:%S"), wrapping across days the same way a datetime's time
 * component would. Returns null for non-finite input.
 */
export function minutesToClockStr(minutes) {
  if (minutes == null || !Number.isFinite(minutes)) return null;
  const dayMinutes = ((Math.round(minutes) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const h = Math.floor(dayMinutes / 60);
  const m = dayMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
}
